import { alpha, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { Bell } from "lucide-react";
import { ReminderBadge } from "web_myplann/Reminders/Components/ReminderBadge";
import { ReminderRemoveButton } from "web_myplann/Reminders/Components/ReminderRemoveButton";
import { ReminderRowMeta } from "web_myplann/Reminders/Components/ReminderRowMeta";
import { REMINDER_ICON_BOX_SIZE } from "web_myplann/Reminders/Config/reminderIconBox";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

type Props = {
	reminder: Reminder;
	highlighted?: boolean;
	onTap?: () => void;
	onRemove?: () => void;
	id?: string;
};

export const ReminderFlatRow = ({
	reminder,
	highlighted = false,
	onTap,
	onRemove,
	id,
}: Props) => {
	const theme = useTheme();

	return (
		<Stack
			id={id}
			direction="row"
			alignItems="center"
			pr={onRemove ? theme.spacingTokens.lg : 0}
			bgcolor={highlighted ? alpha(theme.palette.blue[50], 0.7) : "transparent"}
			sx={{
				transition: "background-color 300ms",
				"& + &": {
					borderTop: theme.colors.border.solid(theme.palette.divider),
				},
			}}
		>
			<Stack
				component={onTap ? ButtonBase : "div"}
				onClick={onTap}
				direction="row"
				alignItems="center"
				flex={1}
				minWidth={0}
				textAlign="left"
				px={theme.spacingTokens.lg}
				py={theme.spacingTokens.md}
				gap={theme.spacingTokens.md}
				sx={
					onTap
						? { "&:active": { bgcolor: theme.palette.grey[50] } }
						: undefined
				}
			>
				<IconBox bg={theme.palette.blue[50]} size={REMINDER_ICON_BOX_SIZE}>
					<Bell size={theme.iconSize.sm} color={theme.palette.blue[600]} />
				</IconBox>

				<Stack flex={1} minWidth={0}>
					<Stack
						direction="row"
						alignItems="center"
						gap={theme.spacingTokens.sm}
						minWidth={0}
					>
						<Typography variant="bodyMedium" noWrap minWidth={0}>
							{reminder.name}
						</Typography>
						<ReminderBadge reminder={reminder} />
					</Stack>
					<ReminderRowMeta reminder={reminder} />
				</Stack>
			</Stack>

			{onRemove && <ReminderRemoveButton onRemove={onRemove} />}
		</Stack>
	);
};
